import React, { type ReactNode } from 'react';
import Button from '@discover-ui/button';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@discover-ui/dialog';
import { cn } from '@discover-lib/utils';
import Separator from '@discover-ui/separator';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import HowItWorksHeader from './HowItWorksHeader';
import HowItWorksSteps from './HowItWorksSteps';
import HowItWorksRestaurantLove from './HowItWorksRestaurantLove';
import HowItWorksQuestions from './HowItWorksQuestions';

interface HowItWorksModalProps {
  transparent?: boolean;
  /**
   * Custom control(s) that open this dialog (use `DialogTrigger asChild` from `@discover-ui/dialog`).
   * When omitted, the default header “How it Works” text button is used.
   */
  renderTrigger?: ReactNode;
  /** Identifies where the modal was opened from, for analytics/experiment goal metrics. */
  trackingSource?: string;
}

const HowItWorksModal = ({ transparent, renderTrigger, trackingSource }: HowItWorksModalProps) => {
  const { track } = useAnalytics();

  return (
    <DialogRoot
      onOpenChange={(open) => {
        if (open) {
          track('how_it_works_opened', {
            ...(trackingSource ? { source: trackingSource } : {}),
          });
        }
      }}>
      {renderTrigger ?? (
        <DialogTrigger asChild>
          <Button
            kind="no-style"
            className={cn(
              'pr-2 text-sm font-normal sm:pr-5 sm:text-[16px] ',
              transparent ? 'text-white' : 'text-brand-blue'
            )}>
            How it Works
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className="min-h-[90vh] max-w-[900px] px-3 py-6 sm:p-12"
        closeIconClassName="size-5 text-gray-700"
        closeButtonClassName="right-1 top-0 size-[44px]">
        <DialogTitle className="sr-only">How it Works</DialogTitle>
        <DialogDescription className="sr-only">
          First Table is a restaurant discovery platform where you get 50% off your meal!
          Restaurants use First Table to fill empty tables. You pay a small booking fee and enjoy a
          great dining experience at half the price.
        </DialogDescription>
        <div className="no-scrollbar flex flex-col overflow-y-auto rounded-md">
          <HowItWorksHeader />
          <Separator className="my-6 sm:my-8" />
          <HowItWorksSteps />
          <Separator className="my-6 sm:my-8" />
          <HowItWorksRestaurantLove />
          <Separator className="my-6 sm:my-8" />
          <HowItWorksQuestions />
          <DialogClose asChild>
            <Button kind="cta" className="mt-6">
              OK, got it
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </DialogRoot>
  );
};

export default HowItWorksModal;
